import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { currentWorkspaceKeys, useApi } from '@/shared/api';
import {
  emptyWizardForm,
  stepFields,
  wizardSchema,
  type WizardForm,
  type WizardStep,
} from './wizardModel';
import { UNTOUCHED, withWorkspaceDefaults, type ObjectValuesTouched } from './wizardRequest';

export function useCreateWizard(preselectedObjectId?: string) {
  const api = useApi();
  const workspace = useQuery({
    queryKey: currentWorkspaceKeys.workspace,
    queryFn: () => api.workspace.get(),
  });
  const [form, setForm] = useState<WizardForm>(() => {
    const base = emptyWizardForm();
    return preselectedObjectId
      ? { ...base, locationMode: 'existing', existingObjectId: preselectedObjectId }
      : base;
  });
  const [step, setStep] = useState<WizardStep>(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Eine eigene Eingabe gewinnt: der Workspace überschreibt sie nicht, wenn er erst danach ankommt,
  // und nur sie geht an den Server — alles andere setzt er selbst aus dem Workspace.
  const touched = useRef<ObjectValuesTouched>({ ...UNTOUCHED });

  const patch = useCallback((partial: Partial<WizardForm>) => {
    if ('timezone' in partial) touched.current.timezone = true;
    if ('currency' in partial) touched.current.currency = true;
    setForm((f) => ({ ...f, ...partial }));
  }, []);

  useEffect(() => {
    if (!workspace.data) return;
    const defaults = workspace.data;
    setForm((f) => withWorkspaceDefaults(f, defaults, touched.current));
  }, [workspace.data]);

  const touchedObjectValues = useCallback(() => ({ ...touched.current }), []);

  const parse = useMemo(() => wizardSchema.safeParse(form), [form]);

  const validateStep = useCallback(
    (target: WizardStep): boolean => {
      const fields = stepFields(target, form.locationMode);
      const next: Record<string, string> = {};
      if (!parse.success) {
        for (const issue of parse.error.issues) {
          const key = String(issue.path[0]);
          if ((fields as string[]).includes(key)) next[key] = issue.message;
        }
      }
      setErrors(next);
      return Object.keys(next).length === 0;
    },
    [form.locationMode, parse],
  );

  const goNext = useCallback(() => {
    if (validateStep(step)) setStep((s) => Math.min(s + 1, 2) as WizardStep);
  }, [step, validateStep]);

  const goBack = useCallback(() => {
    setErrors({});
    setStep((s) => Math.max(s - 1, 0) as WizardStep);
  }, []);

  return {
    form,
    patch,
    step,
    errors,
    parse,
    goNext,
    goBack,
    validateStep,
    canSubmit: parse.success,
    touchedObjectValues,
  };
}
